import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * A filter that takes an id but lets you type a name.
 *
 * Three filters on the old Participants page — provider, user, facility — are
 * async lookups: you type "hjortsh" and pick from five matches, and the form
 * keeps the id. The design draws them as selects, which they cannot be (there are
 * thousands of facilities and no endpoint that lists them), so this is the shape
 * both are reaching for: a text field that searches, and a chosen value that
 * shows its name.
 *
 * The alternative already used once in this tree, on Signups, is a bare id field.
 * It is honest but it costs a trip to another page to find the id first, and
 * these three filters are how anybody narrows a search of hundreds of thousands
 * of rows.
 *
 * `loadOptions` is the api module's own lookup (`lookupByNameOrId`,
 * `lookupByEmailOrId`), which answers `[{ label, value }]` and accepts an id as
 * well as a name. That second part is what lets a pasted URL resolve: with a
 * value and no label, it looks the id up once and shows the name.
 */
export default function LookupSelect({ label, value, onChange, loadOptions, placeholder, hint }) {
  const [text, setText] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(null);

  const boxRef = useRef(null);
  // Guards against a slow response for an earlier keystroke overwriting a later
  // one, which on a debounced lookup happens more often than it sounds.
  const seq = useRef(0);

  const search = useCallback(async key => {
    const mine = seq.current + 1;
    seq.current = mine;
    setBusy(true);
    try {
      const found = await loadOptions(key);
      if (seq.current !== mine) return;
      setOptions(Array.isArray(found) ? found : []);
    } catch (error) {
      if (seq.current === mine) setOptions([]);
    } finally {
      if (seq.current === mine) setBusy(false);
    }
  }, [loadOptions]);

  // A value arrived from the URL: resolve it to a name once, so the filter reads
  // as "Hjortshøj-Egaa" rather than as a GUID somebody has to recognise.
  useEffect(() => {
    if (!value) {
      setChosen(null);
      return;
    }
    if (chosen && chosen.value === value) return;

    let alive = true;
    loadOptions(value)
      .then(found => {
        if (!alive) return;
        const hit = (found || []).find(option => option.value === value) || (found || [])[0];
        setChosen(hit ? { value, label: hit.label } : { value, label: null });
      })
      .catch(() => {
        if (alive) setChosen({ value, label: null });
      });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = event => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!text.trim()) {
      setOptions([]);
      return undefined;
    }
    const timer = setTimeout(() => search(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text, search]);

  const pick = option => {
    setChosen(option);
    setText('');
    setOptions([]);
    setOpen(false);
    onChange(option.value);
  };

  const clear = () => {
    setChosen(null);
    setText('');
    setOptions([]);
    onChange('');
  };

  // A `div`, not a `label`, and that is not cosmetic: a label forwards clicks to
  // its first labelable control, and once a value is chosen that control is the
  // clear button — so picking an option immediately cleared it again.
  return (
    <div className="next-filter next-lookup" ref={boxRef}>
      <span className="next-filter__label">{label}</span>

      {value ? (
        <span className="next-lookup__chosen">
          <span className="next-lookup__name" title={value}>
            {/* No label means the lookup could not resolve it — the id is still
                the filter that is applied, so it is shown rather than hidden. */}
            {chosen?.label || `${String(value).split('-')[0]}…`}
          </span>
          <button type="button" className="next-lookup__clear" onClick={clear} aria-label={`Clear ${label}`}>
            ×
          </button>
        </span>
      ) : (
        <>
          <input
            className="next-input"
            value={text}
            placeholder={placeholder}
            onChange={event => { setText(event.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={event => {
              if (event.key === 'Escape') setOpen(false);
              if (event.key === 'Enter') {
                event.preventDefault();
                if (options[0]) pick(options[0]);
              }
            }}
          />
          {open && (busy || options.length > 0 || text.trim().length > 0) && (
            <div className="next-lookup__menu">
              {busy && <span className="next-lookup__note">Searching…</span>}
              {!busy && options.length === 0 && (
                <span className="next-lookup__note">No match. An id or a name works.</span>
              )}
              {options.map(option => (
                <button
                  type="button"
                  key={option.value}
                  className="next-lookup__option"
                  onClick={() => pick(option)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {hint && <span className="next-form__hint">{hint}</span>}
    </div>
  );
}

LookupSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  loadOptions: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  // Shown under the field — for saying *what* is being searched when it is not
  // the obvious list (the historical report's own facility table, say).
  hint: PropTypes.string
};
